//src/pages/gro/DimensionFormPage.tsx
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  useCreateDimension,
  useDimensionDetail,
  useUpdateDimension,
} from "@/hooks/useDimensions";
import { usePermission } from "@/hooks/usePermission";
import { PERMISSIONS } from "@/lib/permissions";
import type { DimensionUpsertIn, DimensionRiskTheme } from "@/types/dimensions";

const emptyForm: DimensionUpsertIn = {
  code: "",
  name: "",
  description: "",
  risk_theme: "psychosocial",
  score_direction: "higher_is_better",
  is_active: true,
};

function normalizeDimensionCode(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 4))
    .join("_")
    .slice(0, 40);
}

const RISK_THEME_LABELS: Record<DimensionRiskTheme, string> = {
  psychosocial: "Psicossocial",
  physical: "Físico",
  chemical: "Químico",
  biological: "Biológico",
  ergonomic: "Ergonômico",
  accident: "Acidente",
  other: "Outro",
};

export default function DimensionFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const canWrite = usePermission(PERMISSIONS.QUESTIONNAIRES_WRITE);
  const isCreating = !id;
  const isReadOnly = !isCreating && !canWrite;

  const detailQ = useDimensionDetail(id);
  const createMut = useCreateDimension();
  const updateMut = useUpdateDimension();

  const [form, setForm] = useState<DimensionUpsertIn>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [codeTouched, setCodeTouched] = useState(false);

  useEffect(() => {
    if (!detailQ.data) return;
    setForm({
      code: detailQ.data.code,
      name: detailQ.data.name,
      description: detailQ.data.description || "",
      risk_theme: detailQ.data.risk_theme,
      score_direction: detailQ.data.score_direction,
      is_active: detailQ.data.is_active,
    });
    setCodeTouched(true);
  }, [detailQ.data]);

  function goBack() {
    navigate("/gro/dimensions");
  }

  function updateField<K extends keyof DimensionUpsertIn>(
    field: K,
    value: DimensionUpsertIn[K],
  ) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleNameChange(value: string) {
    setForm((prev) => ({
      ...prev,
      name: value,
      code: codeTouched ? prev.code : normalizeDimensionCode(value),
    }));
  }

  async function handleSave() {
    if (isReadOnly) return;
    if (!form.name.trim()) {
      toast.error("Informe o nome da dimensão.");
      return;
    }
    if (!form.code.trim()) {
      toast.error("Informe o código da dimensão.");
      return;
    }

    setSaving(true);
    try {
      if (id) {
        await updateMut.mutateAsync({ id, body: form });
        toast.success("Dimensão atualizada com sucesso.");
      } else {
        await createMut.mutateAsync(form);
        toast.success("Dimensão criada com sucesso.");
      }
      navigate("/gro/dimensions");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar dimensão.");
    } finally {
      setSaving(false);
    }
  }

  if (!isCreating && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isCreating
                  ? "Nova Dimensão"
                  : isReadOnly
                    ? "Visualizar Dimensão"
                    : "Editar Dimensão"}
              </h1>
              <p className="text-sm text-muted-foreground">
                Cadastro de dimensões, responsável por agrupar perguntas dos questionários e utilizado para sugestão automática de riscos.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              Voltar
            </Button>

            {!isReadOnly ? (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Salvar
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados da dimensão</CardTitle>
        </CardHeader>

        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Nome *</Label>
            <Input
              className="mt-1"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ex.: Apoio da liderança"
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label>Código *</Label>
            <Input
              className="mt-1"
              value={form.code}
              onChange={(e) => {
                setCodeTouched(true);
                updateField("code", normalizeDimensionCode(e.target.value));
              }}
              placeholder="Ex.: APOIO_LIDER"
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label>Tema *</Label>
            <Select
              value={form.risk_theme}
              onValueChange={(value) => updateField("risk_theme", value as DimensionRiskTheme)}
              disabled={isReadOnly}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(RISK_THEME_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Status</Label>
            <Select
              value={form.is_active ? "active" : "inactive"}
              onValueChange={(value) => updateField("is_active", value === "active")}
              disabled={isReadOnly}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativa</SelectItem>
                <SelectItem value="inactive">Inativa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Descrição</Label>
            <Textarea
              className="mt-1 min-h-[140px]"
              value={form.description ?? ""}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Descreva o que esta dimensão mede e como ela deve ser interpretada."
              disabled={isReadOnly}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
